import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Chip,
  Fade,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  AutoStories,
  Add,
  CheckCircle,
  PlayArrow,
} from '@mui/icons-material';
import axios from 'axios';

function StoriesList() {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0); // 0 = active, 1 = completed

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/stories`, {
        params: { userId: 'princess-nabilah' }, // TODO: Get from auth context
        headers: { Authorization: `Bearer ${token}` },
      });
      setStories(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stories:', error);
      setLoading(false);
    }
  };

  const activeStories = stories.filter(s => !s.isCompleted);
  const completedStories = stories.filter(s => s.isCompleted);

  const displayedStories = activeTab === 0 ? activeStories : completedStories;

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: '#FAF3E0',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#8D7B68', mb: 3 }} />
          <Typography variant="h6" sx={{ color: '#8D7B68', fontWeight: 600 }}>
            Loading your tales...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#FAF3E0', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Fade in timeout={600}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <AutoStories sx={{ fontSize: 64, color: '#8D7B68', mb: 2 }} />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: '#333',
                mb: 2,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
              }}
            >
              Your Royal Tales
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#555',
                fontSize: { xs: '1rem', sm: '1.1rem' },
                mb: 3,
              }}
            >
              Your collection of enchanting adventures and legendary quests
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/story/new')}
              sx={{
                backgroundColor: '#8D7B68',
                color: '#FFF',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#7A6859',
                  transform: 'scale(1.03)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Begin New Tale
            </Button>
          </Box>
        </Fade>

        {/* Tabs */}
        <Box sx={{ borderBottom: 2, borderColor: '#8D7B68', mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                color: '#8D7B68',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
              },
              '& .Mui-selected': {
                color: '#8D7B68 !important',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#8D7B68',
                height: 3,
              },
            }}
          >
            <Tab
              label={`Active Tales (${activeStories.length})`}
              icon={<PlayArrow />}
              iconPosition="start"
            />
            <Tab
              label={`Completed Tales (${completedStories.length})`}
              icon={<CheckCircle />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Stories Grid */}
        {displayedStories.length === 0 ? (
          <Fade in timeout={800}>
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 3,
                backgroundColor: '#F7EEDC',
                border: '2px solid #8D7B68',
                borderRadius: 3,
              }}
            >
              <AutoStories sx={{ fontSize: 80, color: '#8D7B6860', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#8D7B68', fontWeight: 600, mb: 1 }}>
                {activeTab === 0 ? 'No Active Tales' : 'No Completed Tales'}
              </Typography>
              <Typography variant="body1" sx={{ color: '#555', mb: 3 }}>
                {activeTab === 0
                  ? 'Begin a new adventure to start your journey, Your Majesty!'
                  : 'Complete your active tales to see them here!'}
              </Typography>
              {activeTab === 0 && (
                <Button
                  variant="outlined"
                  onClick={() => navigate('/story/new')}
                  sx={{
                    borderColor: '#8D7B68',
                    color: '#8D7B68',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#8D7B68',
                      color: '#FFF',
                      borderColor: '#8D7B68',
                    },
                  }}
                >
                  Start Your First Tale
                </Button>
              )}
            </Box>
          </Fade>
        ) : (
          <Grid container spacing={3}>
            {displayedStories.map((story, index) => (
              <Grid item xs={12} sm={6} md={4} key={story._id}>
                <Fade in timeout={800 + index * 100}>
                  <Card
                    sx={{
                      height: '100%',
                      backgroundColor: '#F4ECE3',
                      border: '2px solid #8D7B68',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 8px 24px rgba(141, 123, 104, 0.3)',
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(`/story/${story._id}`)}
                      sx={{ height: '100%' }}
                    >
                      {/* Story Image */}
                      <CardMedia
                        component="img"
                        height="200"
                        image={story.scenes[0]?.imageUrl || ''}
                        alt={story.title}
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardContent>
                        {/* Genre Chip */}
                        <Chip
                          label={story.genre}
                          size="small"
                          sx={{
                            backgroundColor: '#8D7B68',
                            color: '#FFF',
                            fontWeight: 600,
                            mb: 1,
                          }}
                        />

                        {/* Title */}
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: '#333',
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {story.title}
                        </Typography>

                        {/* Progress */}
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#555' }}>
                              Progress
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#555', fontWeight: 600 }}>
                              Chapter {story.currentScene} / ~22
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              height: 6,
                              backgroundColor: '#DDD',
                              borderRadius: 3,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${(story.currentScene / 22) * 100}%`,
                                height: '100%',
                                backgroundColor: '#8D7B68',
                              }}
                            />
                          </Box>
                        </Box>

                        {/* Status */}
                        <Typography variant="body2" sx={{ color: '#555' }}>
                          {story.isCompleted ? (
                            <span style={{ color: '#4CAF50', fontWeight: 600 }}>
                              ✓ Complete
                            </span>
                          ) : (
                            `Last updated: ${new Date(story.updatedAt).toLocaleDateString()}`
                          )}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}

export default StoriesList;

